import activityDashboardStyles from './activity-dashboard.styles.css?inline';
import '../../components/state-badge/agwa-state-badge.js';
import { LitElement, html, nothing, TemplateResult, unsafeCSS } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type {
  ActiveSessionDto,
  SessionActivityDto,
  ActivitySummaryDto,
  ActivitySummaryFilterHints,
  GetActiveSessionsResult,
  DataTableFilterDescriptor as ApiFilterDescriptor,
} from '../../api/agwa-api-model';
import { ApiActivity } from '../../api/core/api-activity';
import { ApiFactory } from '../../api/agwasuite-api-factory';
import { getSseHub } from '../../utility/notifications/sse-hub';

import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/select/select.js';
import '@shoelace-style/shoelace/dist/components/option/option.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/format-date/format-date.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/tab-group/tab-group.js';
import '@shoelace-style/shoelace/dist/components/tab/tab.js';
import '@shoelace-style/shoelace/dist/components/tab-panel/tab-panel.js';
import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '../../components/data-table/data-table';
import '../../components/empty-state/agwa-empty-state';
import { styles as sharedStyles } from '../../styles/shared-styles';
import { WithUserProfileConsumer } from '../../mixins/with-user-profile-consumer';
import { WithNetworkState } from '../../mixins/with-network-state';
import { terms } from '../../language/terms';

import type {
  DataTableColumnDef,
  DataTable,
} from '../../components/data-table/data-table';
import type {
  DataTableDataProvider,
  DataTableFilterDescriptor,
} from '../../components/data-table/data-table-provider';
import {
  isSelectionEmpty,
  selectedValues,
  type FilterFacet,
  type FilterSelection,
} from '../../components/filter/filter-facets';
import '../../components/filter/agwa-filter-rail';
import '../../components/filter/agwa-filter-summary';
import '../../components/layout/agwa-fullscreen-block';
import '../../components/search/agwa-search';
import '../../components/user/username';

/** The summary facet key is the server's filter field name. */
const FACET_PERIOD = 'periodType';

/**
 * User Activity Dashboard component.
 * Displays user activity tracking with three tabs:
 * - Live Sessions: Currently connected users (real-time)
 * - Session History: Historical session records with filtering
 * - Activity Summary: Aggregated usage statistics
 *
 * Only accessible to AGWA staff with MemberAdmin rights.
 */
@customElement('activity-dashboard')
export class ActivityDashboard extends WithNetworkState(
  WithUserProfileConsumer(LitElement)
) {
  @state() private activeTab = 'live';

  // Live sessions state
  @state() private liveData?: GetActiveSessionsResult;
  @state() private liveError?: string;
  @state() private liveProvider?: DataTableDataProvider<ActiveSessionDto>;

  // History state
  @state() private historyProvider?: DataTableDataProvider<SessionActivityDto>;
  @state() private historyError?: string;

  // Summary state
  @state() private summaryProvider?: DataTableDataProvider<ActivitySummaryDto>;
  @state() private summaryError?: string;

  // Filters
  @state() private historySearchText = '';
  /** The summary rail's ticks (period type); one `eq` descriptor per tick, read as any-of. */
  @state() private summarySelection: FilterSelection = {};
  @state() private summaryMatchedCount: number | null = null;
  /**
   * The period types any summary row holds, from the server. The aggregator writes Hourly
   * rows only today, and a facet offering Daily was advertising a value no row could match
   * (QA session finding, 2026-09-06); the facet lists what exists.
   */
  @state() private summaryHints: ActivitySummaryFilterHints | null = null;

  static styles = [sharedStyles, unsafeCSS(activityDashboardStyles)];

  // ==================== Live Sessions Columns ====================
  private get liveColumns(): DataTableColumnDef[] {
    return [
      {
        title: terms.loginUser.value,
        fieldName: 'userDisplayName',
        width: '360px',
        sortable: true,
        fixed: 'left',
        // Named by id through the directory, as every person is (D-151, contact rule 8).
        displayValue: (_table: DataTable, item: ActiveSessionDto) =>
          html`<user-name .userId=${item.userId}></user-name>`,
      },
      {
        title: terms.loginGroup.value,
        fieldName: 'groupName',
        width: '360px',
        sortable: true,
        displayValue: (_table: DataTable, item: ActiveSessionDto) =>
          html`${item.groupName ?? '—'}`,
      },
      {
        title: 'Duration',
        fieldName: 'durationSeconds',
        width: '100px',
        sortable: true,
        displayValue: (_table: DataTable, item: ActiveSessionDto) =>
          html`<span
            style="color: var(--sl-color-success-600); font-weight: var(--sl-font-weight-semibold);"
            >${this.formatDuration(item.durationSeconds)}</span
          >`,
      },
      {
        title: 'Connected Since',
        fieldName: 'connectedAt',
        width: '160px',
        sortable: true,
        displayValue: (_table: DataTable, item: ActiveSessionDto) =>
          item.connectedAt
            ? html`<sl-format-date
                date=${item.connectedAt}
                month="short"
                day="numeric"
                hour="2-digit"
                minute="2-digit"
              ></sl-format-date>`
            : html`—`,
      },
      {
        title: 'IP Address',
        fieldName: 'ipAddress',
        width: '130px',
        displayValue: (_table: DataTable, item: ActiveSessionDto) =>
          html`${item.ipAddress ?? '—'}`,
      },
    ];
  }

  // ==================== History Columns ====================
  private get historyColumns(): DataTableColumnDef[] {
    return [
      {
        title: 'Connected',
        fieldName: 'connectedAt',
        width: '160px',
        sortable: true,
        fixed: 'left',
        displayValue: (_table: DataTable, item: SessionActivityDto) =>
          item.connectedAt
            ? html`<sl-format-date
                date=${item.connectedAt}
                month="short"
                day="numeric"
                hour="2-digit"
                minute="2-digit"
              ></sl-format-date>`
            : html`—`,
      },
      {
        title: terms.loginUser.value,
        fieldName: 'userDisplayName',
        width: '360px',
        sortable: true,
        displayValue: (_table: DataTable, item: SessionActivityDto) =>
          html`<user-name .userId=${item.userId}></user-name>`,
      },
      {
        title: terms.loginGroup.value,
        fieldName: 'groupName',
        width: '360px',
        sortable: true,
        displayValue: (_table: DataTable, item: SessionActivityDto) =>
          html`${item.groupName ?? '—'}`,
      },
      {
        title: 'Duration',
        fieldName: 'durationSeconds',
        width: '100px',
        sortable: true,
        displayValue: (_table: DataTable, item: SessionActivityDto) =>
          html`${this.formatDuration(item.durationSeconds)}`,
      },
      {
        title: 'Disconnected',
        fieldName: 'disconnectedAt',
        width: '160px',
        sortable: true,
        displayValue: (_table: DataTable, item: SessionActivityDto) =>
          item.disconnectedAt
            ? html`<sl-format-date
                date=${item.disconnectedAt}
                month="short"
                day="numeric"
                hour="2-digit"
                minute="2-digit"
              ></sl-format-date>`
            : html`<agwa-state-badge state="Active"></agwa-state-badge>`,
      },
      {
        title: 'IP Address',
        fieldName: 'ipAddress',
        width: '130px',
        displayValue: (_table: DataTable, item: SessionActivityDto) =>
          html`${item.ipAddress ?? '—'}`,
      },
    ];
  }

  // ==================== Summary Columns ====================
  private get summaryColumns(): DataTableColumnDef[] {
    return [
      {
        title: 'Period',
        fieldName: 'periodStart',
        width: '180px',
        sortable: true,
        fixed: 'left',
        displayValue: (_table: DataTable, item: ActivitySummaryDto) =>
          item.periodStart
            ? html`<sl-format-date
                date=${item.periodStart}
                month="short"
                day="numeric"
                year="numeric"
                hour="2-digit"
              ></sl-format-date>`
            : html`—`,
      },
      {
        title: 'Type',
        fieldName: 'periodType',
        width: '80px',
        sortable: true,
        displayValue: (_table: DataTable, item: ActivitySummaryDto) =>
          html`<sl-badge variant="neutral" pill>${item.periodType}</sl-badge>`,
      },
      {
        title: terms.loginUser.value,
        fieldName: 'userDisplayName',
        width: '360px',
        displayValue: (_table: DataTable, item: ActivitySummaryDto) =>
          html`<user-name .userId=${item.userId}></user-name>`,
      },
      {
        title: terms.loginGroup.value,
        fieldName: 'groupName',
        width: '360px',
        displayValue: (_table: DataTable, item: ActivitySummaryDto) =>
          html`${item.groupName ?? '—'}`,
      },
      {
        title: 'Sessions',
        fieldName: 'sessionCount',
        width: '100px',
        sortable: true,
        displayValue: (_table: DataTable, item: ActivitySummaryDto) =>
          html`${item.sessionCount}`,
      },
      {
        title: 'Total Time',
        fieldName: 'totalDurationSeconds',
        width: '120px',
        sortable: true,
        displayValue: (_table: DataTable, item: ActivitySummaryDto) =>
          html`${this.formatDuration(item.totalDurationSeconds)}`,
      },
      {
        title: 'Unique IPs',
        fieldName: 'uniqueIpCount',
        width: '100px',
        displayValue: (_table: DataTable, item: ActivitySummaryDto) =>
          html`${item.uniqueIpCount}`,
      },
    ];
  }

  // ==================== Helpers ====================
  private formatDuration(seconds: number | undefined | null): string {
    if (seconds == null || seconds < 0) return '—';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  }

  // ==================== Live Sessions ====================
  private createLiveProvider() {
    this.liveProvider = async (request) => {
      this.liveError = undefined;
      try {
        const api = ApiFactory.authenticated(ApiActivity);
        const res = await api.getLiveSessions({
          sliceStart: request.sliceStart ?? 0,
          sliceLength: request.sliceLength ?? 100,
          requiresCount: request.sliceStart === 0,
        });

        if (!res.success) {
          throw new Error(res.error || 'Failed to load live sessions');
        }

        this.liveData = res.data;
        const sessions = res.data?.sessions ?? [];

        return {
          items: sessions,
          totalCount: res.data?.totalCount,
          hasMore: false, // Live sessions are always a complete snapshot
        };
      } catch (e) {
        this.liveError = (e as Error).message || 'Failed to load live sessions';
        throw e;
      }
    };
  }

  // ==================== History Provider ====================
  private createHistoryProvider() {
    this.historyProvider = async (request) => {
      this.historyError = undefined;

      try {
        const api = ApiFactory.authenticated(ApiActivity);
        const res = await api.getSessionHistory({
          sliceStart: request.sliceStart ?? 0,
          sliceLength: request.sliceLength ?? 100,
          freeText: this.historySearchText || undefined,
          // THE TABLE IS THE ONLY PLACE THE ORDER IS STATED. This used to substitute
          // `connectedAt` descending whenever the request carried nothing, which is how the dead
          // `.defaultSort` binding in the markup above went unnoticed: the list opened newest
          // first, so nobody looked, while the statement a reader could SEE did nothing. A second
          // copy of an intent, in a place no screen reflects, is what hid the first one.
          sort: request.sort.map((s) => ({
            field: s.field,
            direction: s.direction === 'desc' ? 'Desc' : 'Asc',
          })),
          requiresCount: request.sliceStart === 0,
        });

        if (!res.success) {
          throw new Error(res.error || 'Failed to load session history');
        }

        const items = res.data?.items ?? [];

        return {
          items,
          totalCount: res.data?.totalCount,
          hasMore: res.data?.hasMore ?? false,
        };
      } catch (e) {
        this.historyError =
          (e as Error).message || 'Failed to load session history';
        throw e;
      }
    };
  }

  // ==================== Summary Provider ====================
  private createSummaryProvider() {
    this.summaryProvider = async (request) => {
      this.summaryError = undefined;

      try {
        const filters: DataTableFilterDescriptor<string>[] = [];
        for (const value of selectedValues(
          this.summarySelection,
          FACET_PERIOD
        )) {
          filters.push({ field: FACET_PERIOD, operator: 'eq', value });
        }

        const api = ApiFactory.authenticated(ApiActivity);
        const res = await api.getActivitySummary({
          sliceStart: request.sliceStart ?? 0,
          sliceLength: request.sliceLength ?? 100,
          filters: filters as unknown as ApiFilterDescriptor[],
          // The table states the order; see the history provider above for why no copy of it
          // lives here any more.
          sort: request.sort.map((s) => ({
            field: s.field,
            direction: s.direction === 'desc' ? 'Desc' : 'Asc',
          })),
          requiresCount: request.sliceStart === 0,
        });

        if (!res.success) {
          throw new Error(res.error || 'Failed to load activity summary');
        }

        const items = res.data?.items ?? [];
        if ((request.sliceStart ?? 0) === 0) {
          this.summaryMatchedCount = res.data?.totalCount ?? null;
        }
        if (res.data?.filterHints) {
          this.summaryHints = res.data.filterHints;
        }

        return {
          items,
          totalCount: res.data?.totalCount,
          hasMore: res.data?.hasMore ?? false,
        };
      } catch (e) {
        this.summaryError =
          (e as Error).message || 'Failed to load activity summary';
        throw e;
      }
    };
  }

  // ==================== Lifecycle ====================
  private _activityUnsubscribe?: () => void;

  connectedCallback(): void {
    super.connectedCallback();
    this.createLiveProvider();
    this.createHistoryProvider();
    this.createSummaryProvider();

    // Subscribe to activity:live topic for real-time updates
    this._activityUnsubscribe = getSseHub().subscribeTopic(
      'activity:live',
      (evt) => {
        console.log('[ActivityDashboard] Received activity event:', evt);
        // Refresh the active tab when we receive connect/disconnect events
        this.refreshActiveTab();
      }
    );
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    // Unsubscribe from SSE topic
    this._activityUnsubscribe?.();
    this._activityUnsubscribe = undefined;
  }

  /**
   * Called when live updates are connected again after being down (with or without an outage).
   * Re-subscribes to SSE topic and refreshes data since events may have been missed.
   *
   * Was onNetworkOnline until D-137, when that stopped following the live-update connection: a
   * dropped stream with the server answering is no longer "offline", so the refresh it needs is
   * tied to the live updates returning instead.
   */
  protected onLiveUpdatesResumed(): void {
    console.log('[ActivityDashboard] Live updates resumed - refreshing data');
    // Unsubscribe old handler and resubscribe to ensure topic is active
    this._activityUnsubscribe?.();
    this._activityUnsubscribe = getSseHub().subscribeTopic(
      'activity:live',
      (evt) => {
        console.log('[ActivityDashboard] Received activity event:', evt);
        this.refreshActiveTab();
      }
    );
    // Refresh all data since we may have missed events while offline
    this.refreshActiveTab();
  }

  /** Refresh data for the currently active tab */
  private refreshActiveTab() {
    if (this.activeTab === 'live') {
      const liveTable = this.shadowRoot?.querySelector(
        'sl-tab-panel[name="live"] agwa-lit-table'
      ) as DataTable | null;
      void liveTable?.fetchData();
    } else if (this.activeTab === 'history') {
      const historyTable = this.shadowRoot?.querySelector(
        'sl-tab-panel[name="history"] agwa-lit-table'
      ) as DataTable | null;
      void historyTable?.fetchData();
    } else if (this.activeTab === 'summary') {
      const summaryTable = this.shadowRoot?.querySelector(
        'sl-tab-panel[name="summary"] agwa-lit-table'
      ) as DataTable | null;
      void summaryTable?.fetchData();
    }
  }

  private onTabChange(e: CustomEvent) {
    // Shoelace sl-tab-show event has panel name in e.detail.name
    const tabName = e.detail?.name ?? 'live';
    this.activeTab = tabName;

    // Refresh data for the selected tab
    this.refreshActiveTab();
  }

  private onRefreshLive() {
    const liveTable = this.shadowRoot?.querySelector(
      'sl-tab-panel[name="live"] agwa-lit-table'
    ) as DataTable | null;
    void liveTable?.fetchData();
  }

  /**
   * Re-asks the server for the history by giving its table a new provider. The table is always on
   * the tab once the provider exists, and it reloads when its provider changes (data-table.ts
   * `updated`); asking it to fetch as well sent every refresh and search to the server twice
   * (#398 C15 d).
   */
  private onRefreshHistory() {
    this.createHistoryProvider();
  }

  /** As the history's refresh: the new provider is the reload, so the table is not asked again. */
  private onRefreshSummary() {
    this.createSummaryProvider();
  }

  /**
   * Search applies as it is typed (debounced by agwa-search); there is no Search button. It runs
   * once: the box reports one search twice - `search-input` as the person types and `search-change`
   * when they press Enter or clear - so a value already searched is not searched again (#398 C15
   * d). A clear is reported as `search-clear` and then a change to nothing, so the change finds the
   * box already empty and is the one dropped.
   */
  private handleHistorySearchInput(e: CustomEvent<{ value: string }>) {
    if (e.detail.value === this.historySearchText) return;
    this.historySearchText = e.detail.value;
    this.onRefreshHistory();
  }

  private handleHistorySearchClear() {
    this.historySearchText = '';
    this.onRefreshHistory();
  }

  private handleSummaryFilterChange(
    e: CustomEvent<{ selection: FilterSelection }>
  ) {
    this.summarySelection = e.detail.selection;
    this.onRefreshSummary();
  }

  private handleSummaryFilterClear() {
    this.summarySelection = {};
    this.onRefreshSummary();
  }

  private handleSummaryFilterDrop(e: CustomEvent<{ key: string }>) {
    this.summarySelection = { ...this.summarySelection, [e.detail.key]: [] };
    this.onRefreshSummary();
  }

  /** The summary's one facet: which aggregation periods the rows actually hold. */
  private get summaryFacets(): FilterFacet[] {
    return [
      {
        key: FACET_PERIOD,
        title: 'Period',
        phrase: 'aggregated',
        options: (this.summaryHints?.periodTypes ?? []).map((code) => ({
          value: code,
          label: code,
        })),
        emptyHint: 'No summary rows yet.',
      },
    ];
  }

  // ==================== Render Methods ====================
  private renderStats(): TemplateResult {
    const data = this.liveData;
    return html`
      <div class="stats-row">
        <sl-card class="stat-card">
          <div class="stat-value">${data?.totalCount ?? 0}</div>
          <div class="stat-label">Active Sessions</div>
        </sl-card>
        <sl-card class="stat-card">
          <div class="stat-value">${data?.uniqueUserCount ?? 0}</div>
          <div class="stat-label">Unique ${terms.loginUser.plural}</div>
        </sl-card>
        <sl-card class="stat-card">
          <div class="stat-value">${data?.uniqueGroupCount ?? 0}</div>
          <div class="stat-label">Active ${terms.loginGroup.plural}</div>
        </sl-card>
      </div>
    `;
  }

  private renderLiveTab(): TemplateResult {
    if (this.liveError) {
      return html`
        <agwa-empty-state
          title="Error Loading Sessions"
          description=${this.liveError}
        >
          <sl-button variant="primary" @click=${this.onRefreshLive}>
            <sl-icon slot="prefix" name="arrow-clockwise"></sl-icon>
            Retry
          </sl-button>
        </agwa-empty-state>
      `;
    }

    return html`
      <div class="tab-content">
        ${this.renderStats()}
        <div class="filter-bar">
          <sl-button
            variant="default"
            size="small"
            @click=${this.onRefreshLive}
          >
            <sl-icon slot="prefix" name="arrow-clockwise"></sl-icon>
            Refresh
          </sl-button>
        </div>
        <div class="table-container">
          ${
            this.liveProvider
              ? // NEWEST CONNECTION FIRST, and the header says so. The snapshot's order is the
                // server's alone - `getLiveSessions` carries no sort and `JobGetActiveSessions`
                // orders by ConnectedAt descending every time - so what the page owes here is to
                // NAME the column that order belongs to. Without it every header on a table whose
                // columns are offered as sortable sat unmarked, and nothing on screen said which
                // one the list was in. It replaces a `.defaultSort` binding that named no property
                // of the table and did nothing at all.
                html`<agwa-lit-table
                  fill-height
                  .columns=${this.liveColumns}
                  .dataProvider=${this.liveProvider}
                  sortField="connectedAt"
                  .sortAscending=${false}
                ></agwa-lit-table>`
              : nothing
          }
        </div>
      </div>
    `;
  }

  private renderHistoryTab(): TemplateResult {
    return html`
      <div class="tab-content">
        <div class="filter-bar">
          <div class="filters-left">
            <agwa-search
              size="small"
              .value=${this.historySearchText}
              placeholder="Search by ${terms.loginUser.lower}, ${terms.loginGroup.lower}, or IP..."
              @search-input=${this.handleHistorySearchInput}
              @search-change=${this.handleHistorySearchInput}
              @search-clear=${this.handleHistorySearchClear}
            ></agwa-search>
          </div>
          <sl-button
            variant="default"
            size="small"
            @click=${this.onRefreshHistory}
          >
            <sl-icon slot="prefix" name="arrow-clockwise"></sl-icon>
            Refresh
          </sl-button>
        </div>
        ${
          this.historyError
            ? html`<p class="error">${this.historyError}</p>`
            : nothing
        }
        <div class="table-container">
          ${
            this.historyProvider
              ? // NEWEST SESSION FIRST. A person opens the history to see what just happened, and
                // the log only grows, so opening on the oldest row means scrolling past the whole
                // record to reach the reason they came. The property is `sortAscending`, and the
                // table reads undefined as ASCENDING, so descending is stated rather than left to
                // a default. It replaces a `.defaultSort` binding that named no property of the
                // table; the order only held up because the provider below substituted a copy of
                // this intent that nothing on screen could show.
                html`<agwa-lit-table
                  fill-height
                  .columns=${this.historyColumns}
                  .dataProvider=${this.historyProvider}
                  sortField="connectedAt"
                  .sortAscending=${false}
                ></agwa-lit-table>`
              : nothing
          }
        </div>
      </div>
    `;
  }

  private renderSummaryTab(): TemplateResult {
    return html`
      <!--
        The bar and the list are ONE block that can take the whole viewport in place (D-106).
        The tab strip stays on the page behind: the takeover covers it, and leaving it returns
        to the tab still selected.
      -->
      <div class="tab-content">
        <agwa-fullscreen-block
          .title=${`${terms.loginUser.value} Activity Summary`}
        >
          <div class="filter-bar" slot="header">
            <sl-button
              variant="default"
              size="small"
              @click=${this.onRefreshSummary}
            >
              <sl-icon slot="prefix" name="arrow-clockwise"></sl-icon>
              Refresh
            </sl-button>
          </div>
          ${
            this.summaryError
              ? html`<p class="error" slot="header">${this.summaryError}</p>`
              : nothing
          }
          <!-- The period facet in the rail beside the table, the sentence above the rows. -->
          <div class="list-body">
            <agwa-filter-rail
              storage-key="user-activity-summary"
              .facets=${this.summaryFacets}
              .selection=${this.summarySelection}
              ?can-clear=${!isSelectionEmpty(this.summarySelection)}
              @filter-change=${this.handleSummaryFilterChange}
              @filter-clear=${this.handleSummaryFilterClear}
            ></agwa-filter-rail>
            <div class="list-main">
              <agwa-filter-summary
                .facets=${this.summaryFacets}
                .selection=${this.summarySelection}
                subject="summary rows"
                .shownCount=${this.summaryMatchedCount ?? 0}
                .totalCount=${null}
                @filter-drop=${this.handleSummaryFilterDrop}
              ></agwa-filter-summary>
              ${
                this.summaryProvider
                  ? // LATEST PERIOD FIRST, for the same reason as the history beside it, and
                    // replacing the same dead `.defaultSort` binding.
                    html`<agwa-lit-table
                      fill-height
                      .columns=${this.summaryColumns}
                      .dataProvider=${this.summaryProvider}
                      sortField="periodStart"
                      .sortAscending=${false}
                    ></agwa-lit-table>`
                  : nothing
              }
            </div>
          </div>
        </agwa-fullscreen-block>
      </div>
    `;
  }

  render() {
    return html`
      <div class="dashboard-page">
        <!-- No page title: the app header already carries it (architect, 2026-09-06). -->
        <sl-tab-group @sl-tab-show=${this.onTabChange}>
          <sl-tab slot="nav" panel="live" ?active=${this.activeTab === 'live'}>
            <sl-icon name="broadcast"></sl-icon>
            Live Sessions
          </sl-tab>
          <sl-tab
            slot="nav"
            panel="history"
            ?active=${this.activeTab === 'history'}
          >
            <sl-icon name="clock-history"></sl-icon>
            Session History
          </sl-tab>
          <sl-tab
            slot="nav"
            panel="summary"
            ?active=${this.activeTab === 'summary'}
          >
            <sl-icon name="graph-up"></sl-icon>
            Activity Summary
          </sl-tab>

          <sl-tab-panel name="live">${this.renderLiveTab()}</sl-tab-panel>
          <sl-tab-panel name="history">${this.renderHistoryTab()}</sl-tab-panel>
          <sl-tab-panel name="summary">${this.renderSummaryTab()}</sl-tab-panel>
        </sl-tab-group>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'activity-dashboard': ActivityDashboard;
  }
}
